import { FormattedMessage, type IntlShape } from 'react-intl';
import { Button } from 'reactstrap';
import { UserGeneratedInputType, UserGeneratedInputTypeAttribute } from 'types';
import { getUniqueHtmlId } from 'utils/util/formatter';
import { joinJsonPath } from 'utils/util/jsonpath';
import { stripInvalidHtmlSelectorCharacters } from 'utils/util/util';
import { YYYY_MM_DD_WITH_DASHES_FORMAT } from './DatePicker';
import { LIKERT_SCALE_AS_ARRAY } from './LikertScaleInput';

export const DEFAULT_CHECKED_VALUE = 'Y';
export const DEFAULT_UNCHECKED_VALUE = 'N';

export const BASIC_PERSON_FIELDS = [
  'id',
  'url',
  'avatar',
  'given_name',
  'family_name',
  'full_name',
  'status',
  'title',
  // get email for use with proxy person
  'email',
];

export const DETAILED_PERSON_FIELDS = [
  'function',
  'function_id',
  'location',
  'country',
  'cost_center_id',
  'cost_center',
  'department_id',
  'department',
  'business_unit_id',
  'business_unit',
];

// NOTE: these type names (lowercased, e.g. "textarea") are used in Campaign configurations,
// so we CANNOT change them if they are being used there -- also, if you ADD any here,
// make sure to add them to validators.py where you see this list so custom questions
// added in places like feedback requests will be validated properly
export const INPUT_TYPES = {
  TEXT: 'TEXT', // default (one line simple input), chosen if nothing else matches
  TEXTAREA: 'TEXTAREA',
  RICH_TEXT_EDITOR: 'RICH_TEXT_EDITOR',
  MENTION_EDITOR: 'MENTION_EDITOR',
  MEDIA_UPLOADER: 'MEDIA_UPLOADER',
  SWITCH: 'SWITCH',
  SWITCH_WITH_TIMEFRAME_SELECTOR: 'SWITCH_WITH_TIMEFRAME_SELECTOR',
  TOGGLE: 'TOGGLE',
  YES_NO: 'YES_NO',
  DROPDOWN: 'DROPDOWN',
  SELECT: 'SELECT',
  SELECT_PERSON: 'SELECT_PERSON',
  CHECKBOX: 'CHECKBOX',
  CHECKBOX_MULTI: 'CHECKBOX_MULTI',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  BUTTON_OPTIONS: 'BUTTON_OPTIONS',
  AUTOSUGGEST: 'AUTOSUGGEST',
  TAGS_INPUT: 'TAGS_INPUT',
  DATE_PICKER: 'DATE_PICKER',
  PEOPLE_EDITOR: 'PEOPLE_EDITOR',
  CONTACT_METHOD_EDITOR: 'CONTACT_METHOD_EDITOR',
  LIKERT: 'LIKERT',
  SWITCHBANK: 'SWITCHBANK',
  PERCENTAGE_INPUT: 'PERCENTAGE_INPUT',
  SLIDER: 'SLIDER',
  TABLE_EDITOR: 'TABLE_EDITOR',
  TILE_SELECTOR: 'TILE_SELECTOR',
  QUESTIONS_EDITOR: 'QUESTIONS_EDITOR',
  INCLUDE_EXCLUDE_FILTER: 'INCLUDE_EXCLUDE_FILTER',
  STAR_RATING: 'STAR_RATING',
  EMPLOYEE_NPS: 'EMPLOYEE_NPS',
  EMPLOYEE_NPS_PREVIEW: 'EMPLOYEE_NPS_PREVIEW',
  HIDDEN: 'HIDDEN',

  // for use in campaign phase configurations
  // (note: campaign configs are lowercase but we uppercase before processing)
  SECTION: 'SECTION',
  SKILLS: 'SKILLS',

  // For custom components (may not even be input)
  CUSTOM_INPUT: 'CUSTOM_INPUT',
  // For custom non istantiated components that are wired with the form
  CUSTOM_COMPONENT: 'CUSTOM_COMPONENT',
  // For inputs grouped that can iteract within each others but stored as a flat value on the form
  GROUP: 'GROUP',
};

export const getQueryWithType = (q) =>
  q
    ? { name: q, source_includes: ['id', 'name', 'type', 'visibility'] }
    : { source_includes: ['id', 'name', 'type', 'visibility'] };

export const INPUT_ATTRIBUTES = (formatMessage) => ({
  SKILLS_AND_ACTIVITIES: {
    type: INPUT_TYPES.TAGS_INPUT,
    matchingValidatorNames: ['skills', 'activities'],
    renderCreateOption: (x, setIndex) => {
      // default to skill type
      setIndex('skills');

      // note: we set the index on hover because the clicks are stolen
      // by the react tags input, so this is a hack to let us capture
      // the index
      return {
        name: x,
        object: {
          // NOTE: we do not allow creating an activity on the fly
          // as activities must have at least one contribution to be created
          // to ensure we don't get a lot of trash data
          name: (
            <span className="d-flex" style={{ whiteSpace: 'nowrap' }}>
              <span>
                <span className="d-inline-block fw-normal mt-2">
                  <FormattedMessage
                    id="app.views.widgets.inputs.validate_input.attributes.skills_and_activities.create_skill"
                    defaultMessage='Create skill "{skillName}"'
                    values={{ skillName: x }}
                  />
                </span>
              </span>
            </span>
          ),
        },
      };
    },
    elasticsearchOptions: {
      url: 'get-skills-and-activities-by-name',
      getQuery: getQueryWithType,
    },
    // for when a dropdown type is used instead, e.g. in campaign configurations
    // that use a dropdown list of skills
    valueFromAttribute: 'name',
  },
  SKILLS_AND_CREDENTIALS: {
    type: INPUT_TYPES.TAGS_INPUT,
    matchingValidatorNames: ['skills', 'credentials'],
    renderCreateOption: (x, setIndex) => {
      // default to skill type
      setIndex('skills');

      // note: we set the index on hover because the clicks are stolen
      // by the react tags input, so this is a hack to let us capture
      // the index
      return {
        name: x,
        object: {
          name: (
            <span className="d-flex" style={{ whiteSpace: 'nowrap' }}>
              <span>
                <span className="d-inline-block fw-normal mt-2">
                  <FormattedMessage
                    id="app.views.widgets.inputs.validate_input.attributes.skills_and_credentials.create_element"
                    defaultMessage='Create "{element}" as:'
                    values={{ element: x }}
                  />
                </span>
              </span>
              <Button
                color="light"
                className="btn btn-sm mx-3"
                onMouseOver={() => setIndex('skills')}
              >
                <FormattedMessage
                  id="app.views.widgets.inputs.validate_input.attributes.skills_and_credentials.skill"
                  defaultMessage="Skill"
                />
              </Button>
              <Button
                color="light"
                className="btn btn-sm"
                onMouseOver={() => setIndex('credentials')}
              >
                <FormattedMessage
                  id="app.views.widgets.inputs.validate_input.attributes.skills_and_credentials.credential"
                  defaultMessage="Credential"
                />
              </Button>
            </span>
          ),
        },
      };
    },
    elasticsearchOptions: {
      url: 'get-skills-and-credentials-by-name',
      getQuery: getQueryWithType,
    },
    // for when a dropdown type is used instead, e.g. in campaign configurations
    // that use a dropdown list of skills
    valueFromAttribute: 'name',
  },
  SKILLS: {
    type: INPUT_TYPES.TAGS_INPUT,
    matchingValidatorNames: ['skills'],
    helperText: formatMessage({
      id: 'app.views.widgets.inputs.validate_input.attributes.skills.helper_text',
      defaultMessage: 'For example, "Python" or "writing"',
    }),
    renderCreateOption: (x, setIndex) => {
      // default to skill type
      setIndex('skills');

      return {
        name: x,
        object: {
          name: (
            <span>
              <span className="fw-normal">
                <FormattedMessage
                  id="app.views.widgets.inputs.validate_input.attributes.skills.create_skill"
                  defaultMessage='Create skill "{skillName}"'
                  values={{ skillName: x }}
                />
              </span>
            </span>
          ),
        },
      };
    },
    elasticsearchOptions: {
      url: 'get-skills-by-name',
      getQuery: getQueryWithType,
    },
    // for when a dropdown type is used instead, e.g. in campaign configurations
    // that use a dropdown list of skills
    valueFromAttribute: 'name',
  },
  CREDENTIALS: {
    type: INPUT_TYPES.TAGS_INPUT,
    matchingValidatorNames: ['credentials'],
    helperText: formatMessage({
      id: 'app.views.widgets.inputs.validate_input.attributes.credentials.helper_text',
      defaultMessage: 'For example, "Series 63" or "CEP Level I"',
    }),
    renderCreateOption: (x, setIndex) => {
      // default to credentials type
      setIndex('credentials');

      return {
        name: x,
        object: {
          name: (
            <span>
              <span className="fw-normal">
                {' '}
                <FormattedMessage
                  id="app.views.widgets.inputs.validate_input.attributes.credentials.create_credential"
                  defaultMessage='Create credential "{credentialName}"'
                  values={{ credentialName: x }}
                />
              </span>
            </span>
          ),
        },
      };
    },
    elasticsearchOptions: {
      url: 'get-credentials-by-name',
      getQuery: getQueryWithType,
    },
    // for when a dropdown type is used instead, e.g. in campaign configurations
    // that use a dropdown list of skills
    valueFromAttribute: 'name',
  },
  ACCOUNT_NOTIFICATION_SWITCHBANK: {
    type: INPUT_TYPES.SWITCHBANK,
    checkedValue: DEFAULT_CHECKED_VALUE,
    uncheckedValue: DEFAULT_UNCHECKED_VALUE,
    isHorizontalLayout: true,
    labelClassName: 'fw-bold',
    defaultValue: DEFAULT_CHECKED_VALUE,
  },
});

export const getPeopleOrAllQuery = (q, addSourceIncludes = []) => ({
  name: q,
  source_includes: [...BASIC_PERSON_FIELDS, ...addSourceIncludes],
});

export const getPeopleAdminableQuery = (q, addSourceIncludes = []) => ({
  name: q,
  source_includes: [...BASIC_PERSON_FIELDS, ...addSourceIncludes],
  show_only_adminable: true,
});

export const getPeopleAdminableOrSelfQuery = (q, addSourceIncludes = []) => ({
  name: q,
  source_includes: [...BASIC_PERSON_FIELDS, ...addSourceIncludes],
  show_only_adminable_or_under_person_included: true,
});

export const getPeopleCustomSurveyAdministratorEligibleQuery = (
  q: string,
  addSourceIncludes: string[] = []
) => ({
  name: q,
  source_includes: [...BASIC_PERSON_FIELDS, ...addSourceIncludes],
  show_only_custom_survey_administrator_eligible: true,
});

const UGC_MIN_SELECTIONS_ATTRIBUTE = (
  formatMessage: IntlShape['formatMessage']
): UserGeneratedInputTypeAttribute => ({
  name: 'minSelections',
  label: formatMessage({
    id: 'app.views.widgets.inputs.validate_input.ugc_min_selections.label',
    defaultMessage: 'Min selections',
  }),
  placeholder: 'Optional',
  type: INPUT_TYPES.TEXT,
  // value must be an integer
  formatValueOnSubmit: formatToInteger,
});
const UGC_MAX_SELECTIONS_ATTRIBUTE = (
  formatMessage: IntlShape['formatMessage']
): UserGeneratedInputTypeAttribute => ({
  name: 'maxSelections',
  label: formatMessage({
    id: 'app.views.widgets.inputs.validate_input.ugc_max_selections.label',
    defaultMessage: 'Max selections',
  }),
  placeholder: 'Optional',
  type: INPUT_TYPES.TEXT,
  // value must be an integer
  formatValueOnSubmit: formatToInteger,
});
const UGC_MAX_LENGTH_ATTRIBUTE = (
  formatMessage: IntlShape['formatMessage']
): UserGeneratedInputTypeAttribute => ({
  name: 'maxLength',
  label: formatMessage({
    id: 'app.views.widgets.inputs.validate_input.ugc_max_lenght.label',
    defaultMessage: 'Max length',
  }),
  type: INPUT_TYPES.TEXT,
  // value must be an integer
  formatValueOnSubmit: formatToInteger,
});
const UGC_MIN_SKILLS_ATTRIBUTE = (
  formatMessage: IntlShape['formatMessage']
): UserGeneratedInputTypeAttribute => ({
  name: 'minLength',
  label: formatMessage({
    id: 'app.views.widgets.inputs.validate_input.ugc_min_skills.label',
    defaultMessage: 'Minimum skills',
  }),
  type: INPUT_TYPES.TEXT,
  inputType: 'number',
  minValue: 0,
  // value must be an integer
  formatValueOnSubmit: formatToPositiveInteger,
  displayIf: (question) => {
    return !!question?.required;
  },
});
const UGC_OPTIONS_ATTRIBUTE = (
  formatMessage: IntlShape['formatMessage'],
  options = {}
): UserGeneratedInputTypeAttribute => ({
  // note: DROPDOWN and LIKERT use the name "objects" but
  // MULTIPLE_CHOICE uses "options"
  name: 'objects',
  label: formatMessage({
    id: 'app.views.widgets.inputs.validate_input.ugc_options.objects.label',
    defaultMessage: 'Options',
  }),
  defaultValue: [],
  type: INPUT_TYPES.TABLE_EDITOR,
  columns: [
    {
      field: 'id',
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_options.objects.columns.id.name',
        defaultMessage: 'ID',
      }),
      // we can't pass the function in directly here
      // (it gets called with the wrong context)
      formatValueOnSubmit: (val) => stripInvalidHtmlSelectorCharacters(val),
      columnClassName: 'w-25',
      sortable: false,
      popoverContent: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_options.objects.columns.id.popover_content',

        defaultMessage: 'Unique ID for CSV exports, etc.',
      }),
      type: INPUT_TYPES.TEXT,
      generateDefaultValue:
        // @ts-expect-error
        options.generateDefaultValue ?? (() => getUniqueHtmlId()),
      // @ts-expect-error
      filterOnly: !options.allowEditingId,
      // @ts-expect-error
      editable: !!options.allowEditingId,
    },
    {
      field: 'name',
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_options.objects.columns.name.name',
        defaultMessage: 'Option',
      }),
      columnClassName: 'w-75',
      sortable: false,
      type: INPUT_TYPES.TEXT,
      generateDefaultValue: (index) =>
        formatMessage(
          {
            id: 'app.views.widgets.inputs.validate_input.ugc_options.objects.columns.name.defaultValue',
            defaultMessage: 'Option {optionIndex, number}',
          },
          { optionIndex: index + 1 }
        ),
      placeholder: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_options.objects.columns.name.placeholder',
        defaultMessage: 'Enter an option',
      }),
      // @ts-expect-error
      translationNamespace: options?.translationNamespace,
      jsonPath: joinJsonPath(
        // @ts-expect-error
        options?.jsonPath,
        // @ts-expect-error
        options?.jsonPathSuffixOverride ?? '.options'
      ),
      jsonPathItemGenerator: (basePath, obj) =>
        `${basePath}[?(@.id=='${obj?.id}')].name`,
    },
  ],
});
// NOTE: order is intentional (from what we want them to
// discover first to last, e.g. see Likert first before
// other kinds of multiple choice options)

export const USER_GENERATED_INPUT_TYPE_OPTIONS = (
  formatMessage: IntlShape['formatMessage'],
  hideAdvancedFeatures: boolean = false,
  options: object = {}
): UserGeneratedInputType[] =>
  [
    ...(hideAdvancedFeatures
      ? []
      : [
          {
            id: INPUT_TYPES.SECTION,
            name: formatMessage({
              id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.section.name',
              defaultMessage: 'Section header',
            }),
          },
        ]),
    {
      id: INPUT_TYPES.TEXTAREA,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.text_area.name',
        defaultMessage: 'Plain text',
      }),
      attributes: [
        UGC_PLACEHOLDER_ATTRIBUTE(formatMessage, options),
        UGC_MAX_LENGTH_ATTRIBUTE(formatMessage),
      ],
    },
    {
      id: INPUT_TYPES.RICH_TEXT_EDITOR,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.rich_text_editor.name',
        defaultMessage: 'Rich text (links, photos, etc.)',
      }),
      attributes: [
        UGC_PLACEHOLDER_ATTRIBUTE(formatMessage, options),
        UGC_MAX_LENGTH_ATTRIBUTE(formatMessage),
      ],
    },
    {
      id: INPUT_TYPES.PEOPLE_EDITOR,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.people_editor.name',
        defaultMessage: 'People picker',
      }),
      attributes: [
        UGC_PLACEHOLDER_ATTRIBUTE(formatMessage, options),
        UGC_MIN_SELECTIONS_ATTRIBUTE(formatMessage),
        UGC_MAX_SELECTIONS_ATTRIBUTE(formatMessage),
      ] as UserGeneratedInputTypeAttribute[],
    },
    {
      id: INPUT_TYPES.LIKERT,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.name',
        defaultMessage: 'Likert scale',
      }),
      attributes: [
        {
          name: 'scale',
          label: formatMessage({
            id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.scale.label',
            defaultMessage: 'Heading labels',
          }),
          // NOTE: this variable must come from
          // LikertScaleInput.js instead of Performance.js
          // to avoid circular dependencies that cause
          // this variable to be undefined
          defaultValue: LIKERT_SCALE_AS_ARRAY(formatMessage),
          type: INPUT_TYPES.TABLE_EDITOR,
          addingDisabled: true,
          deletingDisabled: true,
          reorderingDisabled: true,
          columns: [
            {
              name: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.scale.columns.name.name',
                defaultMessage: 'Label',
              }),
              field: 'name',
              columnClassName: 'w-50',
              sortable: false,
              type: INPUT_TYPES.TEXT,
              // @ts-expect-error
              translationNamespace: options?.translationNamespace,
              // @ts-expect-error
              jsonPath: joinJsonPath(options?.jsonPath, '.scale'),
              jsonPathItemGenerator: (basePath, obj) =>
                `${basePath}[?(@.value==${obj?.value})].name`,
            },
            {
              name: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.scale.columns.value.name',
                defaultMessage: 'Value',
              }),
              field: 'value',
              columnClassName: 'w-25',
              sortable: false,
              type: INPUT_TYPES.TEXT,
              editable: false,
            },
          ],
        },
        {
          ...UGC_OPTIONS_ATTRIBUTE(formatMessage),
          // @ts-expect-error
          addingDisabled: options.disableAddingQuestions,
          // @ts-expect-error
          deletingDisabled: options.disableRemovingQuestions,
          label: formatMessage({
            id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.options.label',
            defaultMessage: 'Questions',
          }),
          // values for each likert item are name, value, and helperText
          columns: [
            {
              name: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.options.columns.value.name',
                defaultMessage: 'Question',
              }),
              field: 'value',
              columnClassName: 'w-50',
              type: INPUT_TYPES.TEXT,
              generateDefaultValue: (index) => `Question ${index + 1}`,
              placeholder: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.options.columns.value.placeholder',
                defaultMessage: 'Enter a question',
              }),
              // @ts-expect-error
              translationNamespace: options?.translationNamespace,
              // @ts-expect-error
              jsonPath: joinJsonPath(options?.jsonPath, '.objects'),
              jsonPathItemGenerator: (basePath, obj) =>
                `${basePath}[?(@.name=='${obj?.name}')].value`,
            },
            {
              name: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.options.columns.helper_text.name',
                defaultMessage: 'Hover helper text',
              }),
              field: 'helperText',
              columnClassName: 'w-25',
              type: INPUT_TYPES.TEXT,
              placeholder: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.options.columns.helper_text.placeholder',
                defaultMessage: 'Optional',
              }),
              // @ts-expect-error
              translationNamespace: options?.translationNamespace,
              // @ts-expect-error
              jsonPath: joinJsonPath(options?.jsonPath, '.objects'),
              jsonPathItemGenerator: (basePath, obj) =>
                `${basePath}[?(@.name=='${obj?.name}')].helperText`,
            },
            {
              name: 'ID',
              popoverContent: formatMessage({
                id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.likert_scale.attributes.options.columns.id.popover_content',
                defaultMessage: 'Unique ID for CSV exports, etc.',
              }),
              field: 'name',
              // we can't pass the function in directly here
              // (it gets called with the wrong context)
              formatValueOnSubmit: (val) =>
                stripInvalidHtmlSelectorCharacters(val),
              columnClassName: 'w-25',
              type: INPUT_TYPES.TEXT,
              generateDefaultValue: () => getUniqueHtmlId(),
              filterOnly: true,
              editable: false,
            },
          ],
        },
      ],
    },
    {
      id: INPUT_TYPES.MULTIPLE_CHOICE,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.multiple_choice.name',
        defaultMessage: 'Multiple choice',
      }),
      attributes: [
        {
          ...UGC_OPTIONS_ATTRIBUTE(formatMessage, options),
          // note: DROPDOWN and LIKERT use the name "objects" but
          // MULTIPLE_CHOICE uses "options"
          name: 'options',
          placeholder: formatMessage({
            id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.multiple_choice.attributes.options.placeholder',
            defaultMessage: 'Enter an option',
          }),
        },
      ],
    },
    {
      id: INPUT_TYPES.DROPDOWN,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.dropdown.name',
        defaultMessage: 'Dropdown list',
      }),

      attributes: [
        UGC_OPTIONS_ATTRIBUTE(formatMessage, {
          ...options,
          jsonPathSuffixOverride: '.objects',
        }),
      ],
    },
    {
      id: INPUT_TYPES.DATE_PICKER,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.date_picker.name',
        defaultMessage: 'Date picker',
      }),
      // we want to save YYYY-MM-DD without time/timezone for custom questions to ensure that
      // it's stored to the DB without a timezone so that when rendering responses we don't
      // show different dates to the user in different timezones since this datepicker for UGC
      // is for dates only with no times
      format: YYYY_MM_DD_WITH_DASHES_FORMAT,
      attributes: [UGC_PLACEHOLDER_ATTRIBUTE(formatMessage, options)],
    },
    {
      id: INPUT_TYPES.SWITCH,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.switch.name',
        defaultMessage: 'Yes/no toggle',
      }),
      attributes: [],
    },
    {
      id: INPUT_TYPES.STAR_RATING,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.star_rating.name',
        defaultMessage: 'Star Rating',
      }),
    },
    {
      id: INPUT_TYPES.SKILLS,
      name: formatMessage({
        id: 'app.views.widgets.inputs.validate_input.ugc_input_type_options.skills.name',
        defaultMessage: 'Skills',
      }),
      attributes: [UGC_MIN_SKILLS_ATTRIBUTE(formatMessage)],
    },
  ] as UserGeneratedInputType[]; // we need to keep these in sync with the INPUT_TYPES above for
// use by the campaign editor

export const UGC_PLACEHOLDER_ATTRIBUTE = (
  formatMessage: IntlShape['formatMessage'],
  options: object = {}
): UserGeneratedInputTypeAttribute => ({
  name: 'placeholder',
  label: formatMessage({
    id: 'app.views.widgets.inputs.validate_input.ugc_placeholder.placeholder',
    defaultMessage: 'Placeholder',
  }),
  type: INPUT_TYPES.TEXT,
  // @ts-expect-error
  translationNamespace: options?.translationNamespace,
  // @ts-expect-error
  jsonPath: joinJsonPath(options?.jsonPath, '.placeholder'),
  // @ts-expect-error
  translationDisabled: options?.translationDisabled,
  // @ts-expect-error
  translationDisabledMessage: options?.translationDisabledMessage,
});
export const formatToInteger = (value) => {
  // if value is valid integer return it, otherwise return undefined
  return Number.isInteger(parseInt(value)) ? parseInt(value) : undefined;
};
export const formatToPositiveInteger = (value) => {
  const res = formatToInteger(value);
  // if a number, return undefined if negative
  if (typeof res !== 'undefined') {
    return res < 0 ? undefined : res;
  }
  return res;
};
